import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  ButtonGroup,
  Card,
  Col,
  Placeholder,
  Row,
  Spinner,
  Table,
  ToggleButton,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faBoxesStacked,
  faChartLine,
  faCircleDollarToSlot,
  faMinus,
  faSackDollar,
  faStore,
} from "@fortawesome/free-solid-svg-icons";
import {
  Area,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";

import "./dashboardAnalytics.scss";

const PERIODS = [
  { label: "7 ngày", value: 7 },
  { label: "30 ngày", value: 30 },
  { label: "90 ngày", value: 90 },
];

const PIE_COLORS = [
  "#4e73df",
  "#1cc88a",
  "#36b9cc",
  "#f6c23e",
  "#e74a3b",
  "#858796",
  "#5a5c69",
  "#fd7e14",
];

const fmtNumber = (n) => Number(n || 0).toLocaleString();
const fmtMoney = (n, currency = "USD") => {
  const num = Number(n || 0);
  return `${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`;
};
const fmtCompact = (n) => {
  const num = Number(n || 0);
  if (Math.abs(num) >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (Math.abs(num) >= 1_000) return (num / 1_000).toFixed(1) + "k";
  return String(num);
};

const DeltaBadge = ({ value, invertColor = false }) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return <Badge bg="secondary"><FontAwesomeIcon icon={faMinus} /> —</Badge>;
  }
  const positive = value > 0;
  const negative = value < 0;
  const goodWhenUp = !invertColor;
  const tone = (positive && goodWhenUp) || (negative && !goodWhenUp)
    ? "success"
    : (negative && goodWhenUp) || (positive && !goodWhenUp)
      ? "danger"
      : "secondary";
  const icon = positive ? faArrowUp : negative ? faArrowDown : faMinus;
  return (
    <Badge bg={`${tone}-subtle`} text={tone} className="delta-badge">
      <FontAwesomeIcon icon={icon} className="me-1" />
      {Math.abs(value).toFixed(1)}%
    </Badge>
  );
};

const KpiCard = ({ icon, label, value, sub, delta, accent }) => (
  <Card className={`kpi-card kpi-accent-${accent}`}>
    <Card.Body>
      <div className="d-flex align-items-start justify-content-between">
        <div className="kpi-icon-wrap">
          <FontAwesomeIcon icon={icon} />
        </div>
        <DeltaBadge value={delta} />
      </div>
      <div className="kpi-label mt-3">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub ? <div className="kpi-sub text-muted">{sub}</div> : null}
    </Card.Body>
  </Card>
);

const StatusPill = ({ status }) => {
  const s = String(status || "").toLowerCase();
  let bg = "secondary";
  if (["completed", "delivered", "fulfilled"].includes(s)) bg = "success";
  else if (["processing", "shipped", "awaiting_shipment", "in_transit"].includes(s)) bg = "info";
  else if (["pending", "on-hold", "awaiting_collection"].includes(s)) bg = "warning";
  else if (["cancelled", "canceled", "failed", "refunded"].includes(s)) bg = "danger";
  return <Badge bg={bg} className="text-uppercase">{status || "—"}</Badge>;
};

const CardLoadingBlock = ({ rows = 4 }) => (
  <Placeholder animation="glow">
    {Array.from({ length: rows }).map((_, i) => (
      <Placeholder key={i} xs={[12, 10, 8, 11][i % 4]} className="my-1" />
    ))}
  </Placeholder>
);

const DashboardAnalytics = () => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (data) setRefreshing(true);
      else setLoading(true);
      setError("");
      try {
        const r = await Utils._apiRequest({
          path: Enums.endpoints.stats.DASHBOARD,
          query: { days },
          type: "GET",
        });
        if (cancelled) return;
        if (r?.message && !r?.kpis) {
          throw new Error(r.message);
        }
        setData(r);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const currency = data?.currency?.primary || "USD";
  const kpis = data?.kpis;
  const ts = data?.timeseries || [];
  const statusBreakdown = data?.statusBreakdown || [];
  const platformBreakdown = data?.platformBreakdown || [];
  const topStores = data?.topStores || [];
  const recentOrders = data?.recentOrders || [];

  const tsChart = useMemo(() => ts.map((d) => ({
    day: d.day.slice(5), // MM-DD
    Đơn: d.orders,
    "Doanh thu": d.revenue,
  })), [ts]);

  const periodLabel = PERIODS.find((p) => p.value === days)?.label || `${days} ngày`;

  return (
    <div className="dashboard-analytics">
      {/* Header bar */}
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
        <div>
          <h5 className="mb-1">Tổng quan kinh doanh</h5>
          <small className="text-muted">
            {periodLabel} gần nhất • so sánh với {periodLabel} trước đó
            {data?.currency?.all?.length > 1 ? (
              <span className="ms-2">• Tiền tệ chính: <strong>{currency}</strong></span>
            ) : null}
          </small>
          {data?.excludedStatuses?.length ? (
            <div className="text-muted small mt-1" style={{ fontStyle: "italic" }}>
              * KPI đã loại các đơn có trạng thái:{" "}
              <span title={data.excludedStatuses.join(", ")}>
                {data.excludedStatuses.slice(0, 6).join(", ")}
                {data.excludedStatuses.length > 6 ? "…" : ""}
              </span>
            </div>
          ) : null}
        </div>
        <div className="d-flex align-items-center gap-2">
          {refreshing ? <Spinner size="sm" animation="border" /> : null}
          <ButtonGroup>
            {PERIODS.map((p) => (
              <ToggleButton
                key={p.value}
                id={`period-${p.value}`}
                type="radio"
                variant="outline-primary"
                size="sm"
                name="period"
                value={p.value}
                checked={days === p.value}
                onChange={() => setDays(p.value)}
              >
                {p.label}
              </ToggleButton>
            ))}
          </ButtonGroup>
        </div>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      {/* KPI tiles */}
      <Row className="g-3 mb-3">
        <Col xs={12} sm={6} lg={3}>
          {loading ? (
            <Card className="kpi-card"><Card.Body><CardLoadingBlock /></Card.Body></Card>
          ) : (
            <KpiCard
              icon={faBoxesStacked}
              accent="primary"
              label="Tổng đơn hàng"
              value={fmtNumber(kpis?.orders.current)}
              sub={`Kỳ trước: ${fmtNumber(kpis?.orders.previous)}`}
              delta={kpis?.orders.delta}
            />
          )}
        </Col>
        <Col xs={12} sm={6} lg={3}>
          {loading ? (
            <Card className="kpi-card"><Card.Body><CardLoadingBlock /></Card.Body></Card>
          ) : (
            <KpiCard
              icon={faSackDollar}
              accent="success"
              label="Doanh thu"
              value={fmtMoney(kpis?.revenue.current, currency)}
              sub={`Kỳ trước: ${fmtMoney(kpis?.revenue.previous, currency)}`}
              delta={kpis?.revenue.delta}
            />
          )}
        </Col>
        <Col xs={12} sm={6} lg={3}>
          {loading ? (
            <Card className="kpi-card"><Card.Body><CardLoadingBlock /></Card.Body></Card>
          ) : (
            <KpiCard
              icon={faCircleDollarToSlot}
              accent="info"
              label="Giá trị TB / đơn"
              value={fmtMoney(kpis?.avgOrderValue.current, currency)}
              sub={`Kỳ trước: ${fmtMoney(kpis?.avgOrderValue.previous, currency)}`}
              delta={kpis?.avgOrderValue.delta}
            />
          )}
        </Col>
        <Col xs={12} sm={6} lg={3}>
          {loading ? (
            <Card className="kpi-card"><Card.Body><CardLoadingBlock /></Card.Body></Card>
          ) : (
            <KpiCard
              icon={faStore}
              accent="warning"
              label="Cửa hàng có đơn"
              value={fmtNumber(kpis?.activeStores.current)}
              sub={`Kỳ trước: ${fmtNumber(kpis?.activeStores.previous)}`}
              delta={kpis?.activeStores.delta}
            />
          )}
        </Col>
      </Row>

      {/* Trend chart + status donut */}
      <Row className="g-3 mb-3">
        <Col lg={8}>
          <Card className="h-100">
            <Card.Header className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faChartLine} />
              <strong>Xu hướng đơn & doanh thu</strong>
            </Card.Header>
            <Card.Body style={{ height: 360 }}>
              {loading ? (
                <CardLoadingBlock rows={6} />
              ) : tsChart.length === 0 ? (
                <div className="text-center text-muted py-5"><i>Chưa có dữ liệu trong kỳ.</i></div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={tsChart} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#1cc88a" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#1cc88a" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tickFormatter={fmtCompact} tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tickFormatter={fmtCompact} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v, name) => name === "Doanh thu" ? fmtMoney(v, currency) : fmtNumber(v)} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="Doanh thu"
                      yAxisId="right"
                      stroke="#1cc88a"
                      fill="url(#revFill)"
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="Đơn"
                      yAxisId="left"
                      stroke="#4e73df"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card className="h-100">
            <Card.Header><strong>Trạng thái đơn</strong></Card.Header>
            <Card.Body className="status-card-body">
              {loading ? (
                <CardLoadingBlock rows={5} />
              ) : statusBreakdown.length === 0 ? (
                <div className="text-center text-muted py-5"><i>Không có đơn.</i></div>
              ) : (
                <>
                  <div className="status-chart-wrap">
                    <ResponsiveContainer width="100%" height={210}>
                      <PieChart>
                        <Pie
                          data={statusBreakdown}
                          dataKey="count"
                          nameKey="status"
                          innerRadius={48}
                          outerRadius={80}
                          paddingAngle={2}
                        >
                          {statusBreakdown.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="status-legend mt-2">
                    {statusBreakdown.map((row, i) => (
                      <div className="d-flex justify-content-between align-items-center small py-1" key={row.status}>
                        <span className="text-truncate me-2">
                          <span className="legend-dot" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                          <StatusPill status={row.status} />
                        </span>
                        <strong>{fmtNumber(row.count)}</strong>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Platform + Top stores */}
      <Row className="g-3 mb-3">
        <Col lg={4}>
          <Card className="h-100">
            <Card.Header><strong>Theo nền tảng</strong></Card.Header>
            <Card.Body>
              {loading ? <CardLoadingBlock rows={3} /> : platformBreakdown.length === 0 ? (
                <div className="text-muted small"><i>Không có dữ liệu.</i></div>
              ) : (
                platformBreakdown.map((p, i) => {
                  const totalOrders = platformBreakdown.reduce((s, x) => s + x.orders, 0) || 1;
                  const pct = (p.orders / totalOrders) * 100;
                  return (
                    <div className="platform-row" key={p.flatform || i}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="fw-semibold text-capitalize">{p.flatform || "Khác"}</span>
                        <small className="text-muted">{fmtNumber(p.orders)} đơn • {fmtMoney(p.revenue, currency)}</small>
                      </div>
                      <div className="progress" style={{ height: 8 }}>
                        <div
                          className="progress-bar"
                          role="progressbar"
                          style={{ width: `${pct}%`, background: PIE_COLORS[i % PIE_COLORS.length] }}
                          aria-valuenow={pct}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={8}>
          <Card className="h-100">
            <Card.Header><strong>Top cửa hàng</strong></Card.Header>
            <Table hover responsive className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th width={40}>#</th>
                  <th>Cửa hàng</th>
                  <th width={120}>Nền tảng</th>
                  <th width={100} className="text-end">Đơn</th>
                  <th width={140} className="text-end">Doanh thu</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td colSpan={5}><Placeholder animation="glow"><Placeholder xs={10} /></Placeholder></td>
                    </tr>
                  ))
                ) : topStores.length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-muted">Không có dữ liệu.</td></tr>
                ) : (
                  topStores.map((s, i) => (
                    <tr key={s._id}>
                      <td className="text-muted">{i + 1}</td>
                      <td>
                        <Link to={`/stores/${s._id}`} className="fw-semibold text-decoration-none">
                          {s.storeFriendlyName || s.storeName || "(unnamed store)"}
                        </Link>
                      </td>
                      <td><Badge bg="light" text="dark" className="text-capitalize">{s.flatform || "—"}</Badge></td>
                      <td className="text-end">{fmtNumber(s.orders)}</td>
                      <td className="text-end fw-semibold">{fmtMoney(s.revenue, currency)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card>
        </Col>
      </Row>

      {/* Recent orders */}
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Đơn gần nhất</strong>
          <Link to="/orders" className="small text-decoration-none">Xem tất cả →</Link>
        </Card.Header>
        <Table hover responsive className="mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th>Cửa hàng</th>
              <th width={140}>Nền tảng</th>
              <th width={140}>Trạng thái</th>
              <th width={170}>Thời gian</th>
              <th width={140} className="text-end">Doanh thu</th>
              <th width={80}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}><td colSpan={6}><Placeholder animation="glow"><Placeholder xs={10} /></Placeholder></td></tr>
              ))
            ) : recentOrders.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-muted">Chưa có đơn nào.</td></tr>
            ) : (
              recentOrders.map((o) => (
                <tr key={o._id}>
                  <td className="text-truncate" style={{ maxWidth: 280 }}>{o.storeName || "—"}</td>
                  <td><Badge bg="light" text="dark" className="text-capitalize">{o.flatform || "—"}</Badge></td>
                  <td><StatusPill status={o.status} /></td>
                  <td className="small text-muted">{Utils.dateFormat(o.orderDate)}</td>
                  <td className="text-end">{fmtMoney(o.revenue, o.currency || currency)}</td>
                  <td className="text-end">
                    <Link to={`/orders/${o._id}`} className="small">Xem</Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>
    </div>
  );
};

export default DashboardAnalytics;
